import React, { useEffect, useState } from 'react';
import { Box, Button, Tooltip, Grid, Switch } from '@mui/material';
import ExpressionEditor from './ExpressionEditor';
import { DtDataGrid, EditConfig } from '../../ui/dataGrid/DtDataGrid';
import { DtPageHeader } from '../../ui/header';
import { IExpression } from '../../../models/configuration/plugin/IConfiguration';
import { DataGridActionConfig, IDataColumn } from '../../ui/dataGrid';
import { PlayCircleOutline } from '@mui/icons-material';
import { DtStatusDialog } from '../../ui/dialog';
import { useSnackbar } from '../../../hooks/useSnackbar';
import '../../../assets/style.css'; // <-- make sure this import is present for global styles
import DtAlert from '../../ui/alert/DtAlert';
import { DtExpandableText, DtViewableText } from '../../ui/text';

interface ExpressionProps {
    expressions: IExpression[] | undefined,
    updateParentExpressions: (expressions: IExpression[]) => void,
    title?: string;
    showPageHeader?: boolean; 
    previewLength?: number;
    responsivePageConfig?: {
        heightRatio?: number;
        rowHeight?: number;
        reservedHeight?: number;
    };
}

const initialExpressionState: IExpression = {
    ExpressionName: '',
    ExpressionString: '',
    ExpressionMessage: '',
    Enabled: true
};


const ExpressionsControl: React.FC<ExpressionProps> = ({
    expressions,
    updateParentExpressions,
    title,
    showPageHeader = false,
    previewLength = 65,
    responsivePageConfig = { heightRatio: 0.75 }
}) => {
    const [expressionList, setExpressionList] = useState<IExpression[]>([]);
    const [selectedExpression, setSelectedExpression] = useState<IExpression>(initialExpressionState);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState<string | null>(null);
    const [pendingUpdate, setPendingUpdate] = useState<IExpression | null>(null);

    // Dialogs & Alerts
    const [warningDialogOpen, setWarningDialogOpen] = useState(false);
    const [displayDialogOpen, setDisplayDialogOpen] = useState(false);
    const [testTitle, setTestTitle] = useState('');
    const [testResult, setTestResult] = useState('');
    const [warningTitle, setWarningTitle] = useState('');
    const [warningMessage, setWarningMessage] = useState('');
    const { showSnackbar, snackbar } = useSnackbar();

    useEffect(() => {
        if (expressions && JSON.stringify(expressions) !== JSON.stringify(expressionList)) {
            setExpressionList(expressions);
            setSelectedExpression(initialExpressionState);
            setSelectedIndex(null);
        }
    }, [expressions]);

    // Select row handler
    const handleRowSelect = (row: IExpression, rowIndex: number) => {
        setSelectedExpression(row);
        setSelectedIndex(rowIndex);
    };

    // DataGrid columns
    const columns: IDataColumn[] = [
       
        {
            name: "Name",
            fieldId: "ExpressionName",
            datatype: "text",
            required: true,
            sortable: true,
            renderCell: (row: IExpression, rowIndex: number) =>
                <DtExpandableText value={row.ExpressionName} showToggleLabel={false}/>,
        },
        {
            name: "Message",
            fieldId: "ExpressionMessage",
            datatype: "text",
            required: true,
            sortable: true,
            renderCell: (row: IExpression, rowIndex: number) =>
                <DtExpandableText value={row.ExpressionMessage} showToggleLabel={false}/>,
        },
        {
            name: "Expression",
            fieldId: "ExpressionString",
            datatype: "text",
            required: true,
            sortable: true,
            renderCell: (row: IExpression, rowIndex: number) =>
                <DtViewableText
                    modalTitle='Expression String'
                    value={row.ExpressionString}
                    viewContent={<Box >{row.ExpressionString}</Box>
                    }
                />,
        },
        {
            name: "Enable",
            fieldId: "Enabled",
            datatype: "checkbox",
            width: 10,
            required: false,
            sortable: false,
            renderCell: (row: IExpression, rowIndex: number) => (
                <Switch
                    color='primary'
                    checked={row.Enabled}
                    onChange={e => {
                        const updatedExpression = { ...row, Enabled: e.target.checked };
                        // Directly update Enabled, do not check for duplicate name
                        let updatedExpressionsList = [...expressionList];
                        updatedExpressionsList[rowIndex] = updatedExpression;
                        setExpressionList(updatedExpressionsList);
                        updateParentExpressions(updatedExpressionsList);
                    }}
                    size="small"
                />
            )
        },
    ];

    // DataGrid actions
    const actionsConfig: DataGridActionConfig = {
        mode: 'inline',
        actions: [
            {
                type: "custom",
                label: "Test",
                icon: <Tooltip title="Test Expression"><PlayCircleOutline fontSize='small' /></Tooltip>,
                onClick: (row: IExpression) => {
                    setDisplayDialogOpen(true);
                    setTestTitle("Result");
                    setTestResult(`Testing Expression ${row.ExpressionName}`);
                }
            }
        ]
    };

    // Edit row config
    const editConfig: EditConfig = {
        mode: 'custom',
        onEdit: (row: IExpression, rowIndex: number) => handleOpen('edit', row, rowIndex)
    };

    // Delete row config
    const deleteConfig = {
        confirm: true,
        confirmTitle: "Delete Expression",
        confirmMessage: "Are you sure you want to delete the selected expression?",
        confirmYesLabel: "Delete",
        confirmNoLabel: "Cancel",
        onDelete: (row: IExpression, rowIndex: number) => {
            updateExpressions(row, 'delete');
            showSnackbar('Item deleted successfully.', 'success');
        }
    };

    // Update expressions (add/edit/delete)
    const updateExpressions = (expression: IExpression, mode: string, rowIndex?: number): string | null => {
        try {
            let updatedExpressionsList = [...expressionList];
            switch (mode) {
                case 'add': {
                    const exists = updatedExpressionsList.find(exp => exp.ExpressionName.trim() === expression.ExpressionName.trim());
                    if (!exists) {
                        updatedExpressionsList.push(expression);
                        setSelectedExpression(expression);
                        setSelectedIndex(updatedExpressionsList.length - 1);
                    } else {
                        setWarningDialogOpen(true);
                        setWarningTitle('Invalid Expression Name');
                        setWarningMessage(`${expression.ExpressionName} already exists!`);
                    }
                    break;
                }
                case 'edit': {
                    // Find if the new name matches any other expression (excluding current index)
                    const duplicateExpIndex = updatedExpressionsList.findIndex((exp, index) =>
                        exp.ExpressionName.trim() === expression.ExpressionName.trim() && index !== selectedIndex);

                    if (typeof selectedIndex === "number" && selectedIndex >= 0) {
                        if (duplicateExpIndex === -1) {
                            // No duplicate, update normally
                            updatedExpressionsList[selectedIndex] = expression;
                        } else {
                            // Duplicate found, show warning and do NOT update
                            setWarningDialogOpen(true);
                            setWarningTitle('Duplicate Expression Name');
                            setWarningMessage(`An expression with the name "${expression.ExpressionName}" already exists. Please choose a unique name.`);
                            return null; // Do not update
                        }
                    }
                    break;
                }
                case 'delete':
                    updatedExpressionsList = updatedExpressionsList.filter(exp => exp.ExpressionName !== expression.ExpressionName);
                    setSelectedExpression(initialExpressionState);
                    setSelectedIndex(null);
                    break;
                default:
                    return "Unknown mode";
            }
            setExpressionList(updatedExpressionsList);
            updateParentExpressions(updatedExpressionsList);
            return mode === 'add' ? "Added" : mode === 'edit' ? "Updated" : "Deleted";
        } catch (error) {
            console.error("Error updating expressions: ", error);
            return "Error occurred";
        } finally {
            setOpen(false);
        }
    };

    // Dialogs
    const handleWarningClose = () => {
        setWarningDialogOpen(false);
        if (pendingUpdate && typeof selectedIndex === "number" && selectedIndex >= 0) {
            const updatedList = [...expressionList];
            updatedList[selectedIndex] = pendingUpdate;
            setExpressionList(updatedList);
            setSelectedExpression(pendingUpdate);
            setPendingUpdate(null);
            updateParentExpressions(updatedList);
        } else {
            setPendingUpdate(null);
        }
    };

    // Editor open/close
    const handleOpen = (mode: string, row?: IExpression, rowIndex?: number) => {
        setOpen(true);
        setMode(mode);
        setSelectedExpression(row ?? initialExpressionState);
        setSelectedIndex(typeof rowIndex === "number" ? rowIndex : null);
    };
    const handleClose = () => setOpen(false);

    return (
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {showPageHeader ? (
                <>
                    <DtPageHeader
                        actions={
                            <Button size='small' variant="contained" color="primary" onClick={() => handleOpen('add')}>
                                Add Expression
                            </Button>
                        }
                    >
                        Send Validations
                    </DtPageHeader>
                    <DtAlert severity='warning'>
                        {title ?? "Any 'False' evaluations will Halt a Docutech request"}
                    </DtAlert>
                </>
            ) : (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={9}>
                        <DtAlert severity='warning'>
                            {title ?? "Any 'False' evaluations will Halt a Docutech request"}
                        </DtAlert>
                    </Grid>
                    <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Button
                            size='small'
                            variant="contained"
                            color="primary"
                            onClick={() => handleOpen('add')}
                        >
                            Add Expression
                        </Button>
                    </Grid>
                </Grid>
            )}

            <Box sx={{position: 'relative'}}>
                <Grid container spacing={2}>
                    {/* Left: DataGrid */}
                    <Grid item xs={12}>
                        <DtDataGrid
                            columns={columns}
                            data={expressionList}
                            actionsConfig={actionsConfig}
                            editConfig={editConfig}
                            deleteConfig={deleteConfig}
                            paged={true}
                            highlightSelectedRow={true}
                            onRowSelect={handleRowSelect}
                            responsivePageSize
                            responsivePageConfig={responsivePageConfig}
                            emptyState={{minHeight: 200}}
                        />
                    </Grid>
                </Grid>
            </Box>
            {open && (
                <ExpressionEditor
                    expression={selectedExpression}
                    onValidate={() => { }}
                    onSave={updateExpressions}
                    editorMode={mode}
                    open={open}
                    onCloseDialog={handleClose}
                    renderMode='dialog'
                />
            )}
            <DtStatusDialog
                status='warning'
                open={warningDialogOpen}
                title={warningTitle}
                onClose={handleWarningClose}
                message={warningMessage}
            />
            <DtStatusDialog
                open={displayDialogOpen}
                title={testTitle}
                message={testResult}
                onClose={() => setDisplayDialogOpen(false)}
            />
            {snackbar}
        </Box>
    );
};

export default ExpressionsControl;